import { ActionContext } from "vuex";
import { AppletType, OTAStatus } from "../../../common/src/enums";

export default {
  namespaced: true,
  state: {
    filter: {
      id: undefined,
      type: undefined,
      external_device_id: undefined,
      operator: undefined,
      cm_platform: undefined,
      modified_start: undefined,
      modified_end: undefined,
    },
    items: [] as any[],
    updated_index: -1,
    sim_detail: undefined,
    auto_generated_iccid: undefined,
  },
  getters: {
    getItems: (state: any) => state.items || [],
    getUpdatedIndex: (state: any) => state.updated_index,
  },
  mutations: {
    APPLY_FILTER: (state: any, payload: any) => (state.filter = payload),
    SET_ITEMS: (state: any, payload: any) => (state.items = payload),
    SET_SIM_DETAIL: (state: any, payload: any) => (state.sim_detail = payload),
    SET_UPDATED_INDEX: (state: any, payload: any) => (state.updated_index = payload),
    SET_AUTO_GENERATED_ICCID: (state: any, payload: string) => (state.auto_generated_iccid = payload),
  },
  actions: {
    applyFilter: ({ commit }: ActionContext<any, any>, payload: any) => commit("APPLY_FILTER", payload),
    updateByWebSocket: ({ state, commit }: ActionContext<any, any>, payload: any) => {
      const targetIndex = state.items.findIndex((item: any) => item.id === payload.sim.id);

      commit("SET_UPDATED_INDEX", targetIndex);

      const nextItems = [...state.items];
      if (targetIndex !== -1) {
        nextItems[targetIndex] = {
          ...nextItems[targetIndex],
          ...payload.sim,
        };
        commit("SET_ITEMS", nextItems);
      } else if (payload.sim.created_at === payload.sim.updated_at) {
        // NOTE: スマホ等端末側からのアクセスによりSIMのデータが自動生成された場合にこの処理が走る
        commit("SET_AUTO_GENERATED_ICCID", payload.sim.iccid);
      }
    },
    setSimDetail: ({ commit }: ActionContext<any, any>, payload: any) => commit("SET_SIM_DETAIL", payload),
    refreshItems: ({ state, commit }: ActionContext<any, any>) => {
      commit("SET_ITEMS", [...state.items]);
    },
    clearUpdatedIndex: ({ commit }: ActionContext<any, any>) => {
      commit("SET_UPDATED_INDEX", -1);
    },
    resetAutoGeneratedIccid: ({ commit }: ActionContext<any, any>) => {
      commit("SET_AUTO_GENERATED_ICCID", undefined);
    },
    updateItemsByOTALogEvent: ({ state, commit }: ActionContext<any, any>, payload: any) => {
      // SIM一覧で該当するSIMだけSTATUSを更新しリアルタイムで更新するには、STOREレベルで手動更新するしかない
      // SIM一覧表示用にフロント側で臨時的にstatus値を置き換え、ほかの動作でSimList.vueのfetchList()が実行されたらDBの値に更新される
      // (が、臨時値と一緒のはず)
      const targetIndex = state.items.findIndex((item: any) => item.iccid === payload.data.iccid);
      commit("SET_UPDATED_INDEX", targetIndex);
      const nextItems = [...state.items];

      if (targetIndex !== -1) {
        updateItems(payload, nextItems, targetIndex);
        commit("SET_ITEMS", nextItems);
      }
    },
  },
};

function updateItems(payload: any, nextItems: any[], targetIndex: number) {
  const { appletType, actionType, status } = payload.data;
  const poller = nextItems[targetIndex].poller;
  const other = nextItems[targetIndex].other_applet;
  switch (appletType) {
    case AppletType.Poller:
      if (actionType === "INSTALL_PACKAGE") {
        poller.install_progress.status = status;
        poller.ota_progress.status = calculateFinalStatus(poller.uninstall_progress?.status, poller.install_progress?.status)[0];
        poller.ota_progress.action_type.type = calculateFinalStatus(poller.uninstall_progress?.status, poller.install_progress?.status)[1];
      } else if (actionType === "REMOVE_PACKAGE") {
        poller.uninstall_progress.status = status;
        poller.ota_progress.status = calculateFinalStatus(poller.uninstall_progress?.status, poller.install_progress?.status)[0];
        poller.ota_progress.action_type.type = calculateFinalStatus(poller.uninstall_progress?.status, poller.install_progress?.status)[1];
      }
      break;
    case AppletType.Other:
      if (actionType === "INSTALL_PACKAGE") {
        other.install_progress.status = status;
        other.ota_progress.status = calculateFinalStatus(other.uninstall_progress?.status, other.install_progress?.status)[0];
        other.ota_progress.action_type.type = calculateFinalStatus(other.uninstall_progress?.status, other.install_progress?.status)[1];
      } else if (actionType === "REMOVE_PACKAGE") {
        other.uninstall_progress.status = status;
        other.ota_progress.status = calculateFinalStatus(other.uninstall_progress?.status, other.install_progress?.status)[0];
        other.ota_progress.action_type.type = calculateFinalStatus(other.uninstall_progress?.status, other.install_progress?.status)[1];
      } else if (actionType === "STORE_DATA") {
        other.store_data_progress.status = status;
        other.store_data_progress.action_type.type = "STORE_DATA";
      } else if (actionType === "RFM") {
        other.rfm_progress.status = status;
        other.rfm_progress.action_type.type = "RFM";
      }
      break;
  }
}

function calculateFinalStatus(uninstall_status: string, install_status: string) {
  if (
    [OTAStatus.Ready, OTAStatus.Started, OTAStatus.Interrupted, OTAStatus.Failed, OTAStatus.Succeeded].includes(uninstall_status as OTAStatus) &&
    !install_status
  ) {
    return [uninstall_status, "REMOVE_PACKAGE"];
  }
  if (!uninstall_status && install_status === OTAStatus.NotFound) {
    return [install_status, "INSTALL_PACKAGE"];
  }
  if (uninstall_status === OTAStatus.Ready && install_status === OTAStatus.Ready) {
    return [install_status, "INSTALL_PACKAGE"];
  }
  if (
    [OTAStatus.Blocked, OTAStatus.Started, OTAStatus.Interrupted, OTAStatus.Failed].includes(uninstall_status as OTAStatus) &&
    install_status === OTAStatus.Ready
  ) {
    return [uninstall_status, "REMOVE_PACKAGE"];
  }
  if (
    uninstall_status === OTAStatus.Succeeded &&
    [OTAStatus.Ready, OTAStatus.Started, OTAStatus.Interrupted, OTAStatus.Failed, OTAStatus.Succeeded].includes(install_status as OTAStatus)
  ) {
    return [install_status, "INSTALL_PACKAGE"];
  }
  return [install_status, "INSTALL_PACKAGE"];
}
